import type { TradeCycleVersion } from '@festina/unitlink-general-types/unitlink'
import type { Optional } from '@festina-life/core'
import { assertNotEmpty, dateUtils } from '@festina-life/core'
import { EmptyState } from '@festina-life/ui/feedback'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { useNavigate } from 'react-router'

import { useHook } from '../../lenteHooks'
import { generalRoutes } from '../../routing/generalRoutes'
import { TradeCycleFiltersForm } from './TradeCycleFiltersForm'
import type { TradeCycleFilterFormValues } from './tradeCycleFilterUtils'

type Props = {
  tradeCycleChains: TradeCycleVersion[][]
  selectedTradeCycle: Optional<TradeCycleVersion>
  filterValues: TradeCycleFilterFormValues
  onSearch: (values: TradeCycleFilterFormValues) => void
}

const defineColumn = createColumnDefiner<TradeCycleVersion>()

export const TradeCycleOverview = ({
  tradeCycleChains,
  selectedTradeCycle,
  filterValues,
  onSearch,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const navigate = useNavigate()
  const tenantId = useHook('routing.tenant.useTenantParam')()

  const columns = {
    fromTime: defineColumn({
      heading: translate('tradeCycleOverview.fromTime'),
      keyPath: 'fromTime',
      formatter: dateUtils.dateWithTimeAsStr,
    }),
    tradeCycleType: defineColumn({
      heading: translate('tradeCycleOverview.tradeCycleType'),
      keyPath: 'tradeCycleType',
      formatter: (type) => translateEnum('unitLink.enum.TradeCycleType', type),
      filterMode: 'auto',
    }),
    status: defineColumn({
      heading: translate('tradeCycleOverview.status'),
      keyPath: 'status.type',
      formatter: (status) => translateEnum('enum.TradeCycleStatus', status),
    }),
  }

  return (
    <Card>
      <CardHeader title={translate('tradeCycleOverview.title')} />
      <CardBody>
        <TradeCycleFiltersForm
          initialValues={filterValues}
          onSearch={onSearch}
        />
      </CardBody>
      {!tradeCycleChains.length && (
        <CardBody>
          <EmptyState itemName={translate('tradeCycleOverview.title')} />
        </CardBody>
      )}
      {tradeCycleChains.map((cycles) => (
        <CardBody key={cycles.at(-1)?.tradeCycleId}>
          <SortableTable
            hasLeftAlignedColumns
            columns={columns}
            data={cycles}
            rowOnClick={(row) => {
              assertNotEmpty(row.tradeCycleId)

              return navigate(
                generalRoutes.tenant.tradeCycle({
                  tenantId,
                  tradeCycleId: row.tradeCycleId,
                }),
              )
            }}
            rowIsSelected={(tradeCycle) =>
              tradeCycle.tradeCycleId === selectedTradeCycle?.tradeCycleId
            }
          />
        </CardBody>
      ))}
    </Card>
  )
}
